import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/fda";

export default function Settings() {
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "AGENT", password: "" });
  const [alerts, setAlerts] = useState(null);

  const load = () => {
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/audit-logs").then(({ data }) => setLogs(data)).catch(() => {});
    api.get("/settings/lead-alerts").then(({ data }) => setAlerts(data)).catch(() => {});
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ email: "", name: "", role: "AGENT", password: "" });
      load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const toggle = async (u) => {
    try { await api.patch(`/users/${u.id}/active`, { active: !u.active }); load(); }
    catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-settings-page">
      <h1 className="font-display text-3xl font-extrabold text-forest">Settings</h1>

      <section className="mt-8 rounded-2xl border border-border bg-white p-6 text-sm text-forest-soft" data-testid="lead-alerts-card">
        <h2 className="font-display text-lg font-bold text-forest">New lead email alerts</h2>
        {alerts?.enabled ? (
          <p className="mt-2">Every new lead triggers an instant internal email to
            <strong className="text-forest" data-testid="lead-alert-email"> {alerts.lead_alert_email}</strong>,
            containing the FDA reference, contact details and assessment answers. Change the recipient with the
            <code> LEAD_ALERT_EMAIL</code> setting.</p>
        ) : (
          <p className="mt-2">Lead email alerts are not configured.</p>
        )}
      </section>

      <section className="mt-8 rounded-2xl border border-border bg-white p-6">
        <h2 className="font-display text-lg font-bold text-forest">Create user</h2>
        <form onSubmit={create} className="mt-4 grid gap-3 sm:grid-cols-4">
          <input required placeholder="Name" data-testid="new-user-name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border-2 border-border px-3 py-2 text-sm" />
          <input required type="email" placeholder="Email" data-testid="new-user-email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg border-2 border-border px-3 py-2 text-sm" />
          <select data-testid="new-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="rounded-lg border-2 border-border px-3 py-2 text-sm">
            {["AGENT", "ADMIN", "SUPER_ADMIN"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input required type="password" placeholder="Temporary password" data-testid="new-user-password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-lg border-2 border-border px-3 py-2 text-sm" />
          <button type="submit" data-testid="create-user-button"
            className="rounded-full bg-terracotta px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider text-white sm:col-span-4 sm:justify-self-start">
            Create user
          </button>
        </form>
      </section>

      <section className="mt-6 overflow-x-auto rounded-2xl border border-border bg-white" data-testid="users-table">
        <table className="w-full min-w-[700px] text-left text-sm">
          <thead className="bg-sand-deep text-xs font-bold uppercase tracking-wider text-forest-soft">
            <tr>{["Name", "Email", "Role", "Active", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-semibold text-forest">{u.name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3 text-xs font-bold">{u.role}</td>
                <td className="px-4 py-3">{u.active ? "Yes" : "No"}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggle(u)} data-testid={`toggle-user-${u.email}`}
                    className="rounded-full border-2 border-border px-3 py-1.5 text-[10px] font-bold uppercase text-forest">
                    {u.active ? "Disable" : "Enable"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-lg font-bold text-forest">Audit log</h2>
        <div className="mt-3 max-h-[500px] overflow-y-auto rounded-2xl border border-border bg-white" data-testid="audit-log-table">
          <table className="w-full text-left text-sm">
            <thead className="bg-sand-deep text-xs font-bold uppercase tracking-wider text-forest-soft">
              <tr>{["When", "User", "Action", "Record", "Detail"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-border">
                  <td className="px-4 py-2 text-xs">{new Date(l.timestamp).toLocaleString("en-GB")}</td>
                  <td className="px-4 py-2 text-xs">{l.user_name}</td>
                  <td className="px-4 py-2 text-xs font-bold">{l.action}</td>
                  <td className="px-4 py-2 text-xs">{l.affected_record || "—"}</td>
                  <td className="px-4 py-2 text-xs text-forest-soft">{Object.keys(l.metadata || {}).length ? JSON.stringify(l.metadata) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-10 rounded-2xl border border-border bg-white p-6 text-sm text-forest-soft">
        <h2 className="font-display text-lg font-bold text-forest">Data retention</h2>
        <p className="mt-2">Lead information is retained until the customer requests removal, subject to applicable
          legal and regulatory requirements; otherwise 3 years (configured by <code>LEAD_RETENTION_YEARS</code>).
          Records are never automatically deleted by the application.</p>
      </section>
    </div>
  );
}
